/* eslint-disable @atlaskit/design-system/no-legacy-icons */
/* eslint-disable filenames/match-regex */
import { DatabaseFieldIconType, FIELD_ICONS } from '@databases/icons/FieldTypeIcon';
import { getFieldTypeName } from '@databases/intl/field-type-names';
import { messages } from '@databases/intl/messages';
import { DefaultRootState } from '@databases/redux';
import { DatabaseFieldType, ValueWithContext } from '@databases/types/database';
import { getReferencedLabelContentRefs } from '@databases/utils/database/field-types/pageLabels';
import { getReferencedStatusContentRefs } from '@databases/utils/database/field-types/pageStatus';
import React, { ComponentType } from 'react';
import { MessageDescriptor } from 'react-intl-next';

import AttachmentValueEdit from './edit/AttachmentValueEdit';
import CheckboxValueEdit from './edit/CheckboxValueEdit';
import DateValueEdit from './edit/DateValueEdit';
import EntryLinkValueEdit from './edit/EntryLinkValueEdit';
import HyperlinkValueEdit from './edit/HyperlinkValueEdit';
import JiraIssueValueEdit from './edit/JiraIssueValueEdit';
import NumberValueEdit from './edit/NumberValueEdit';
import NumberValuePopoverEdit from './edit/NumberValuePopoverEdit';
import PageLabelsValueEdit from './edit/PageLabelsValueEdit';
import PageStatusValueEdit from './edit/PageStatusValueEdit';
import SelectValueEdit from './edit/SelectValueEdit';
import TextValueEdit from './edit/TextValueEdit';
import TextValuePopoverEdit from './edit/TextValuePopoverEdit';
import UserValueEdit from './edit/UserValueEdit';
import EntryLinkPopoverEdit from './edit/entry-link-popover/EntryLinkPopoverEdit';
import HyperlinkValuePopoverEdit from './edit/hyperlink-popover/HyperlinkPopoverEdit';
import JiraIssueValuePopoverEdit from './edit/jira-issue-popover/JiraIssuePopoverEdit';
import PageLabelsPopoverEdit from './edit/page-labels-popover/PageLabelsPopoverEdit';
import PageLinkValueEdit from './edit/page-link/PageLinkValueEdit';
import PageLinkValuePopoverEdit from './edit/page-link-popover/PageLinkPopoverEdit';
import PageStatusPopoverEdit from './edit/page-status-popover/PageStatusPopoverEdit';
import TagPopoverEdit from './edit/tag-popover/TagPopoverEdit';
import { EditComponentProps } from './edit/types';
import UserValuePopoverEdit from './edit/user-popover/UserPopoverEdit';
import AttachmentValueView from './view/AttachmentValueView';
import BacklinkValueView from './view/BacklinkValueView';
import ContentDetailsValueView from './view/ContentDetailsValueView';
import DateValueView from './view/DateValueView';
import EntryDetailsValueView from './view/EntryDetailsValueView';
import EntryLinkValueView from './view/EntryLinkValueView';
import HyperlinkValueView from './view/HyperlinkValueView';
import JiraIssueDetailsValueView from './view/JiraIssueDetailsValueView';
import JiraIssueValueView from './view/JiraIssueValueView';
import NumberValueView from './view/NumberValueView';
import PageLinkValueView from './view/PageLinkValueView';
import SelectValueView from './view/SelectValueView';
import TextValueView from './view/TextValueView';
import UserValueView from './view/UserValueView';
import { ViewComponentProps } from './view/types';

export enum FieldTypeCategory {
  BASIC = 'Basic',
  PAGE = 'Page',
  JIRA = 'Jira',
  OTHER_DATABASES = 'Other Databases',
}

export enum EditMode {
  /** Values of this type are not editable, the view component is always rendered. */
  READ_ONLY,

  /** Values of this type can switch between the view and edit component when it is focused/blurred. */
  INLINE_EDIT,

  /** The (seamless) edit component of this value should always be rendered (if the database is editable). */
  EDIT,
}

export interface FieldType<Type extends DatabaseFieldType> extends DatabaseFieldIconType {
  label: MessageDescriptor;
  description?: MessageDescriptor;
  shortDescription?: MessageDescriptor;
  errorDescription?: MessageDescriptor;
  category: FieldTypeCategory;
  view: ComponentType<ViewComponentProps<Type>>;
  popoverEdit?: ComponentType<EditComponentProps<Type>>;
  edit: ComponentType<EditComponentProps<Type>>;
  editMode: (context: ValueWithContext, state: DefaultRootState) => EditMode;
}

export const getFieldTypes = (
  isLiveDocEnabled?: boolean
): { [Type in DatabaseFieldType]: FieldType<Type> } => {
  return {
    // Basic
    [DatabaseFieldType.TEXT]: {
      ...FIELD_ICONS[DatabaseFieldType.TEXT],
      label: getFieldTypeName(DatabaseFieldType.TEXT),
      shortDescription: messages['field-settings.type-text-short-description'],
      category: FieldTypeCategory.BASIC,
      view: TextValueView,
      edit: TextValueEdit,
      editMode: () => EditMode.INLINE_EDIT,
      popoverEdit: TextValuePopoverEdit,
    },
    [DatabaseFieldType.ATTACHMENT]: {
      ...FIELD_ICONS[DatabaseFieldType.ATTACHMENT],
      label: getFieldTypeName(DatabaseFieldType.ATTACHMENT),
      category: FieldTypeCategory.BASIC,
      view: AttachmentValueView,
      edit: AttachmentValueEdit,
      editMode: () => EditMode.EDIT,
    },
    [DatabaseFieldType.NUMBER]: {
      ...FIELD_ICONS[DatabaseFieldType.NUMBER],
      label: getFieldTypeName(DatabaseFieldType.NUMBER),
      category: FieldTypeCategory.BASIC,
      view: NumberValueView,
      edit: NumberValueEdit,
      editMode: () => EditMode.INLINE_EDIT,
      popoverEdit: NumberValuePopoverEdit,
    },
    [DatabaseFieldType.DATE]: {
      ...FIELD_ICONS[DatabaseFieldType.DATE],
      label: getFieldTypeName(DatabaseFieldType.DATE),
      category: FieldTypeCategory.BASIC,
      view: DateValueView,
      edit: DateValueEdit,
      editMode: () => EditMode.INLINE_EDIT,
    },
    [DatabaseFieldType.SELECT]: {
      ...FIELD_ICONS[DatabaseFieldType.SELECT],
      label: getFieldTypeName(DatabaseFieldType.SELECT),
      shortDescription: messages['field-settings.type-tag-short-description'],
      category: FieldTypeCategory.BASIC,
      view: SelectValueView,
      edit: SelectValueEdit,
      popoverEdit: TagPopoverEdit,
      editMode: () => EditMode.INLINE_EDIT,
    },
    [DatabaseFieldType.USER]: {
      ...FIELD_ICONS[DatabaseFieldType.USER],
      label: getFieldTypeName(DatabaseFieldType.USER),
      shortDescription: messages['field-settings.type-user-short-description'],
      category: FieldTypeCategory.BASIC,
      view: UserValueView,
      edit: UserValueEdit,
      editMode: () => EditMode.INLINE_EDIT,
      popoverEdit: UserValuePopoverEdit,
    },
    [DatabaseFieldType.HYPERLINK]: {
      ...FIELD_ICONS[DatabaseFieldType.HYPERLINK],
      label: getFieldTypeName(DatabaseFieldType.HYPERLINK),
      shortDescription: messages['field-settings.type-link-short-description'],
      category: FieldTypeCategory.BASIC,
      view: HyperlinkValueView,
      edit: HyperlinkValueEdit,
      popoverEdit: HyperlinkValuePopoverEdit,
      editMode: () => EditMode.INLINE_EDIT,
    },
    [DatabaseFieldType.CHECKBOX]: {
      ...FIELD_ICONS[DatabaseFieldType.CHECKBOX],
      label: getFieldTypeName(DatabaseFieldType.CHECKBOX),
      shortDescription: messages['field-settings.type-checkbox-short-description'],
      category: FieldTypeCategory.BASIC,
      view: CheckboxValueEdit,
      edit: (props) => <CheckboxValueEdit recursionCount={0} {...props} />,
      editMode: () => EditMode.INLINE_EDIT,
    },
    [DatabaseFieldType.PAGE_LINK]: {
      ...FIELD_ICONS[DatabaseFieldType.PAGE_LINK],
      label: getFieldTypeName(DatabaseFieldType.PAGE_LINK, isLiveDocEnabled),
      shortDescription: isLiveDocEnabled
        ? messages['field-settings.type-content-link-short-description']
        : messages['field-settings.type-page-link-short-description'],
      category: FieldTypeCategory.PAGE,
      view: PageLinkValueView,
      edit: PageLinkValueEdit,
      editMode: () => EditMode.INLINE_EDIT,
      popoverEdit: PageLinkValuePopoverEdit,
    },
    [DatabaseFieldType.PAGE_STATUS]: {
      ...FIELD_ICONS[DatabaseFieldType.PAGE_STATUS],
      label: getFieldTypeName(DatabaseFieldType.PAGE_STATUS, isLiveDocEnabled),
      description: isLiveDocEnabled
        ? messages['field-settings.type-page-and-live-doc-status-description']
        : messages['field-settings.type-page-status-description'],
      shortDescription: isLiveDocEnabled
        ? messages['field-settings.type-content-status-short-description']
        : messages['field-settings.type-page-status-short-description'],
      errorDescription: isLiveDocEnabled
        ? messages['field-settings.type-status-error-description']
        : messages['field-settings.type-page-status-error-description'],
      category: FieldTypeCategory.PAGE,
      view: (props) => <ContentDetailsValueView {...props} />,
      edit: PageStatusValueEdit,
      editMode: ({ database, field, entry }, state) =>
        !field.settings.allowEdit ||
        getReferencedStatusContentRefs({ database, field, entry }, state).length === 0
          ? EditMode.READ_ONLY
          : EditMode.INLINE_EDIT,
      popoverEdit: PageStatusPopoverEdit,
    },
    [DatabaseFieldType.PAGE_LABELS]: {
      ...FIELD_ICONS[DatabaseFieldType.PAGE_LABELS],
      label: getFieldTypeName(DatabaseFieldType.PAGE_LABELS, isLiveDocEnabled),
      description: isLiveDocEnabled
        ? messages['field-settings.type-page-and-live-doc-label-description']
        : messages['field-settings.type-page-label-description'],
      shortDescription: isLiveDocEnabled
        ? messages['field-settings.type-content-label-short-description']
        : messages['field-settings.type-page-label-short-description'],
      errorDescription: isLiveDocEnabled
        ? messages['field-settings.type-label-error-description']
        : messages['field-settings.type-page-label-error-description'],
      category: FieldTypeCategory.PAGE,
      view: (props) => <ContentDetailsValueView {...props} />,
      edit: PageLabelsValueEdit,
      editMode: ({ database, field, entry }, state) =>
        !field.settings.allowEdit ||
        getReferencedLabelContentRefs({ database, field, entry }, state).length === 0
          ? EditMode.READ_ONLY
          : EditMode.INLINE_EDIT,
      popoverEdit: PageLabelsPopoverEdit,
    },
    [DatabaseFieldType.PAGE_DETAILS]: {
      ...FIELD_ICONS[DatabaseFieldType.PAGE_DETAILS],
      label: getFieldTypeName(DatabaseFieldType.PAGE_DETAILS, isLiveDocEnabled),
      description: isLiveDocEnabled
        ? messages['field-settings.type-page-and-live-doc-details-description']
        : messages['field-settings.type-page-details-description'],
      shortDescription: isLiveDocEnabled
        ? messages['field-settings.type-content-details-short-description']
        : messages['field-settings.type-page-details-short-description'],
      errorDescription: isLiveDocEnabled
        ? messages['field-settings.type-details-error-description']
        : messages['field-settings.type-page-details-error-description'],
      category: FieldTypeCategory.PAGE,
      view: (props) => <ContentDetailsValueView {...props} />,
      edit: () => null,
      editMode: () => EditMode.READ_ONLY,
    },
    [DatabaseFieldType.ENTRY_LINK]: {
      ...FIELD_ICONS[DatabaseFieldType.ENTRY_LINK],
      label: getFieldTypeName(DatabaseFieldType.ENTRY_LINK),
      description: messages['field-settings.type-entry-link-description'],
      shortDescription: messages['field-settings.type-entry-link-short-description'],
      category: FieldTypeCategory.OTHER_DATABASES,
      view: EntryLinkValueView,
      edit: EntryLinkValueEdit,
      popoverEdit: EntryLinkPopoverEdit,
      editMode: () => EditMode.INLINE_EDIT,
    },
    [DatabaseFieldType.ENTRY_DETAILS]: {
      ...FIELD_ICONS[DatabaseFieldType.ENTRY_DETAILS],
      label: getFieldTypeName(DatabaseFieldType.ENTRY_DETAILS),
      description: messages['field-settings.type-entry-details-description'],
      shortDescription: messages['field-settings.type-entry-details-short-description'],
      errorDescription: messages['field-settings.type-entry-details-error-description'],
      category: FieldTypeCategory.OTHER_DATABASES,
      view: EntryDetailsValueView,
      edit: () => null,
      editMode: () => EditMode.READ_ONLY,
    },
    [DatabaseFieldType.BACKLINK]: {
      ...FIELD_ICONS[DatabaseFieldType.BACKLINK],
      label: getFieldTypeName(DatabaseFieldType.BACKLINK),
      description: messages['field-settings.type-entry-backlink-description'],
      shortDescription: messages['field-settings.type-entry-backlink-short-description'],
      category: FieldTypeCategory.OTHER_DATABASES,
      view: BacklinkValueView,
      edit: () => null,
      editMode: () => EditMode.READ_ONLY,
    },
    [DatabaseFieldType.JIRA_ISSUE]: {
      ...FIELD_ICONS[DatabaseFieldType.JIRA_ISSUE],
      label: getFieldTypeName(DatabaseFieldType.JIRA_ISSUE),
      shortDescription: messages['field-settings.type-jira-issue-short-description'],
      category: FieldTypeCategory.JIRA,
      view: JiraIssueValueView,
      edit: JiraIssueValueEdit,
      editMode: () => EditMode.INLINE_EDIT,
      popoverEdit: JiraIssueValuePopoverEdit,
    },
    [DatabaseFieldType.JIRA_ISSUE_DETAILS]: {
      ...FIELD_ICONS[DatabaseFieldType.JIRA_ISSUE_DETAILS],
      label: getFieldTypeName(DatabaseFieldType.JIRA_ISSUE_DETAILS),
      description: messages['field-settings.type-jira-issue-details-description'],
      shortDescription:
        messages['field-settings.type-jira-issue-details-short-description'],
      errorDescription:
        messages['field-settings.type-jira-issue-details-error-description'],
      category: FieldTypeCategory.JIRA,
      view: JiraIssueDetailsValueView,
      edit: (_) => null,
      editMode: () => EditMode.READ_ONLY,
    },
  };
};
